// Presentation / runtime / session / connectivity policies — four independent axes.
// presentationMode comes from the ROUTE (or the Travel preference), runtimeProfile from CONFIG,
// sessionPolicy from the runtime + presentation, connectivity from the browser. Never from screen width.
import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { isTravelMode, useOnline } from "@/lib/travelMode";

export const PRESENTATION = { EVERYDAY: "EVERYDAY", TRAVEL: "TRAVEL", AIRLINE: "AIRLINE" };
export const RUNTIME = { WEB: "WEB", PERSONAL_DEVICE_OFFLINE: "PERSONAL_DEVICE_OFFLINE", AIRLINE_EVALUATION: "AIRLINE_EVALUATION", SUPPLIER_SPECIFIC: "SUPPLIER_SPECIFIC" };
export const SESSION = { PERSONAL: "PERSONAL", ANONYMOUS_SHARED: "ANONYMOUS_SHARED" };

const RUNTIME_KEY = "mwq_runtime_profile";

/** Runtime is configuration: build env, `?runtime=` (persisted for the device), else WEB. */
export function runtimeProfile() {
  try {
    const q = new URLSearchParams(window.location.search).get("runtime");
    if (q && RUNTIME[q]) { localStorage.setItem(RUNTIME_KEY, q); return q; }
    const saved = localStorage.getItem(RUNTIME_KEY);
    if (saved && RUNTIME[saved]) return saved;
  } catch { /* storage unavailable → WEB */ }
  const env = process.env.REACT_APP_RUNTIME_PROFILE;
  return env && RUNTIME[env] ? env : RUNTIME.WEB;
}

export const isAirlinePath = (p) => p === "/airline" || p.startsWith("/airline/");
export const isTravelPath = (p) => p === "/travel" || p.startsWith("/travel/");

export function presentationFor(pathname) {
  if (isAirlinePath(pathname) || airlineSessionActive()) return PRESENTATION.AIRLINE;
  if (isTravelPath(pathname) || isTravelMode()) return PRESENTATION.TRAVEL;
  return PRESENTATION.EVERYDAY;
}

// Airline session flag lives in sessionStorage (tab-scoped) so leaving /airline into an activity keeps airline chrome.
const AIR_KEY = "mwq_airline_session";
export function airlineSessionActive() { try { return sessionStorage.getItem(AIR_KEY) === "1"; } catch { return false; } }
export function setAirlineSession(on) { try { on ? sessionStorage.setItem(AIR_KEY, "1") : sessionStorage.removeItem(AIR_KEY); } catch { /* ignore */ } window.dispatchEvent(new Event("mwq:presentation")); }

export function sessionPolicy(presentation, runtime) {
  return presentation === PRESENTATION.AIRLINE || runtime === RUNTIME.AIRLINE_EVALUATION || runtime === RUNTIME.SUPPLIER_SPECIFIC ? SESSION.ANONYMOUS_SHARED : SESSION.PERSONAL;
}

/** Runtime capabilities are probed, not assumed. */
export function runtimeCapabilities() {
  const caps = { serviceWorker: false, caches: false, localStorage: false, webgl: false, audio: false, speech: false, touch: false, keyboard: true };
  try { caps.serviceWorker = "serviceWorker" in navigator; caps.caches = "caches" in window; } catch { /* ignore */ }
  try { localStorage.setItem("__mwq_probe", "1"); localStorage.removeItem("__mwq_probe"); caps.localStorage = true; } catch { /* ignore */ }
  try { const c = document.createElement("canvas"); caps.webgl = !!(c.getContext("webgl") || c.getContext("experimental-webgl")); } catch { /* ignore */ }
  caps.audio = !!(window.AudioContext || window.webkitAudioContext);
  caps.speech = "speechSynthesis" in window;
  caps.touch = "ontouchstart" in window || (navigator.maxTouchPoints || 0) > 0;
  return caps;
}

export function usePresentation() {
  const { pathname } = useLocation();
  const online = useOnline();
  const [, bump] = useState(0);
  useEffect(() => { const h = () => bump((n) => n + 1); window.addEventListener("mwq:presentation", h); window.addEventListener("mwq:travel-mode", h); return () => { window.removeEventListener("mwq:presentation", h); window.removeEventListener("mwq:travel-mode", h); }; }, []);
  const presentation = presentationFor(pathname);
  const runtime = runtimeProfile();
  return { presentation, runtime, session: sessionPolicy(presentation, runtime), connectivity: online ? "ONLINE" : "OFFLINE", airline: presentation === PRESENTATION.AIRLINE, travel: presentation === PRESENTATION.TRAVEL };
}
